import { useId, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { Wordmark } from "../../brand/Wordmark";
import { useSession } from "../session/SessionContext";
import styles from "./AppShell.module.css";
import { visibleNavItems } from "./nav";
import { SessionSummary } from "./SessionSummary";

export const MAIN_CONTENT_ID = "main-content";

/**
 * Application shell (WEB-007): skip link, maroon sidebar (the only surface the maroon-backed
 * logo works on — BRAND-004), permission-filtered navigation, header, and the <main> landmark.
 * Below 60rem the sidebar becomes a top bar with a menu button.
 */
export function AppShell() {
  const session = useSession();
  const { pathname } = useLocation();
  const navId = useId();
  // The phone menu is open only on the page where it was opened, so navigating closes it in
  // the same render (no effect, no extra render — react.dev "You might not need an effect").
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const menuOpen = openedOn === pathname;

  return (
    <>
      <a href={`#${MAIN_CONTENT_ID}`} className={styles.skipLink}>
        Skip to main content
      </a>
      <div className={styles.shell}>
        <div className={styles.sidebar}>
          <Wordmark width={168} />
          <button
            type="button"
            className={styles.menuButton}
            aria-expanded={menuOpen}
            aria-controls={navId}
            onClick={() => {
              setOpenedOn(menuOpen ? null : pathname);
            }}
          >
            {menuOpen ? "Close menu" : "Menu"}
          </button>
          <nav id={navId} aria-label="Main" className={styles.nav} data-open={menuOpen}>
            <ul className={styles.navList}>
              {visibleNavItems(session).map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end className={styles.navLink}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <header className={styles.header}>
          <SessionSummary />
        </header>
        <main id={MAIN_CONTENT_ID} tabIndex={-1} className={styles.main}>
          <Outlet />
        </main>
      </div>
    </>
  );
}
